import { forwardRef } from 'react';
import clsx from 'clsx';

import { CheckIcon } from 'components/Icons/CheckIcon';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  id: string;
  children: React.ReactNode;
  errorText?: string;
}

const CheckboxComponent: React.ForwardRefRenderFunction<HTMLInputElement, CheckboxProps> = (
  { name, id, children, errorText, ...inputProps },
  ref
) => {
  const checkBoxClass = clsx(
    'flex h-5 min-h-[20px] w-5 min-w-[20px] items-center justify-center border border-gray-300 text-white hover:border-primaryDark hover:ring-1 hover:ring-primaryDark active:border-primaryDark',
    'active:ring-1 active:ring-primaryDark peer-checked:border-primary peer-checked:bg-primary peer-checked:hover:bg-primaryDark peer-checked:active:bg-primaryDark',
    'peer-focus:border-primaryDark peer-focus:ring-1 peer-focus:ring-primaryDark',
    { 'ring-2 ring-error': Boolean(errorText) },
    '[&>svg]:hidden',
    '[&>svg]:peer-checked:block',
    'transition-all duration-200'
  );

  return (
    <label htmlFor={id} data-testid={`${name}-label`} className="cursor-pointer flex-col">
      <div className="flex items-center">
        <input
          type="checkbox"
          name={name}
          id={id}
          data-testid={`${name}-input`}
          aria-invalid={Boolean(errorText)}
          className="peer -z-10 h-0 w-0 opacity-0"
          ref={ref}
          {...inputProps}
        />
        <div data-testid={`${name}-check-box`} className={checkBoxClass}>
          <CheckIcon width={14} height={14} data-testid={`${name}-check-icon`} />
        </div>
        <div data-testid={`${name}-label-text`} className="ml-2.5 text-sm">
          {children}
        </div>
      </div>
      <small
        role="alert"
        data-testid={`${name}-error-text`}
        className="mt-1 inline-block h-4 text-error"
      >
        {errorText}
      </small>
    </label>
  );
};

export const Checkbox = forwardRef(CheckboxComponent);
